import React, { FC, useCallback, useEffect, useState } from 'react'
import 'react-image-crop/dist/ReactCrop.css'
import styles from 'styles/components/form.module.scss'
import image from 'styles/components/image.module.scss'
import text from 'styles/utility/text.module.scss'
import ReactCrop, { Crop } from 'react-image-crop'
import { Spacer } from 'components/Utility'
import { FlexColumn, FlexRow } from 'components/Flex'
import { ArrowButton, BlueButton, GreyButton, FileUploadButton, TextButton } from 'components/Button'
import { CautionModal, ImageModal } from 'components/Modal'
import { uploadImage } from 'lib/storage'
import { hideLoadingAction, showLoadingAction } from 'reducks/loading/actions'
import { useDispatch } from 'react-redux'
import heic2any from 'heic2any'
import { ImageDropzone } from './index'
import classNames from 'classnames'

type Props = {
  folderPath: string
  deleteImageUrls: (index: number) => void
  handleImageUrls: (value: string) => void
  rearrangeImages: (index: number, type: 'prev' | 'next') => void
  imageUrls: string[]
  imageType: 'store' | 'product'
}

const ImagesCrop: FC<Props> = (props) => {
  const { folderPath, deleteImageUrls, handleImageUrls, rearrangeImages, imageUrls, imageType } = props

  const canvas = document.createElement('canvas')
  const dispatch = useDispatch()
  const imageWidth = 375
  const imageHeight = imageType === 'store' ? 240 : 277

  const [imageRef, setImageRef] = useState<HTMLImageElement | null>(null)
  const [open, setOpen] = useState<boolean>(false)
  const [modalType, setModalType] = useState<'size' | 'type' | 'crop' | ''>('')
  const [src, setSrc] = useState<string>('')
  const [fileName, setFileName] = useState<string>('')
  const [fileType, setFileType] = useState<string>('')

  const [acceptedFiles, setAcceptedFiles] = useState<File[]>([])
  const [fileIndex, setFileIndex] = useState<number>(0)
  const handleAcceptedFiles = useCallback(
    (files: File[]) => {
      setAcceptedFiles([...files])
    },
    [setAcceptedFiles]
  )

  // cropの初期値(左上を基準に横幅100%、16:9の比率で切り取り枠を表示)
  const [crop, setCrop] = useState<Crop | undefined>(undefined)

  const onSelectFile = async (file: File): Promise<void> => {
    const reader = new FileReader()
    // 画像形式のチェック
    if (
      file.type !== 'image/jpeg' &&
      file.type !== 'image/jpg' &&
      file.type !== 'image/png' &&
      file.type !== 'image/heic'
    ) {
      setFileName(file.name)
      setModalType('type')
      setOpen(true)
      return
    }
    // ファイルサイズのチェック(5MB以上はアラート表示)
    if (file.size > 5242880) {
      setFileName(file.name)
      setModalType('size')
      setOpen(true)
      return
    }
    // HEIC形式はそのままでは使えないのでJPEGに変換する
    if (file.type === 'image/heic') {
      dispatch(showLoadingAction())
      const blob = file as Blob
      const convertedBlob = await heic2any({
        blob,
        toType: 'image/png',
        quality: 1,
      })
      dispatch(hideLoadingAction())
      reader.readAsDataURL(convertedBlob as Blob)
    } else {
      reader.readAsDataURL(file)
    }

    reader.onload = () => {
      if (typeof reader.result !== 'string') return
      setSrc(reader.result)
      setFileName(file.name)
      setFileType(file.type)
      setModalType('crop')
      setOpen(true)
    }
  }

  const onImageLoaded = (image: HTMLImageElement): boolean => {
    // 横幅100%、375px:240 or 277pxの比率で切り取り枠を表示
    const aspect = imageWidth / imageHeight
    // 幅を375、高さを240 or 277で割った時に、幅が大きければ切り取り比率より縦長の画像
    const width =
      image.width / imageWidth < image.height / imageHeight
        ? image.width
        : image.height * aspect > image.width
        ? image.width
        : image.height * aspect
    // 幅を375、高さを240 or 277で割った時に、幅が大きければ切り取り比率より縦長の画像
    const height =
      image.width / imageWidth > image.height / imageHeight
        ? image.height
        : image.width / aspect > image.height
        ? image.height
        : image.width / aspect
    // 切り取り枠が中央になるよう計算
    const x = (image.width - width) / 2
    const y = (image.height - height) / 2
    setCrop({
      ...{
        unit: 'px',
        width,
        height,
        x,
        y,
        aspect,
      },
    })
    // iOS Safariの場合、画像アップロード直後にデフォルトcropするまでwaitしたいため
    dispatch(showLoadingAction())
    setTimeout(() => {
      dispatch(hideLoadingAction())
      setImageRef(image)
    }, 1000)
    return false
  }

  const onCropChange = (crop: Crop) => {
    setCrop(crop)
  }

  const onCropComplete = (crop: Crop) => {
    if (imageRef && crop.width && crop.height) {
      const scaleX = imageRef.naturalWidth / imageRef.width
      const scaleY = imageRef.naturalHeight / imageRef.height
      canvas.width = crop.width
      canvas.height = crop.height
      const ctx = canvas.getContext('2d')
      if (ctx !== null) {
        ctx.drawImage(
          imageRef,
          (crop.x ?? 0) * scaleX,
          (crop.y ?? 0) * scaleY,
          crop.width * scaleX,
          crop.height * scaleY,
          0,
          0,
          crop.width,
          crop.height
        )
      }
    }
  }

  const closeModal = useCallback(() => {
    setFileName('')
    setFileType('')
    setModalType('')
    setOpen(false)
    setFileIndex((prevState) => prevState + 1)
  }, [setOpen, setModalType, setFileName, setFileType])

  const deleteImage = useCallback(
    (index: number) => {
      setImageRef(null)
      setSrc('')
      setFileName('')
      setFileType('')
      setModalType('')
      deleteImageUrls(index)
    },
    [setImageRef, setSrc, setModalType, setFileName, setFileType]
  )

  const saveCroppedImage = () => {
    canvas.toBlob(async (blob) => {
      if (!blob) return
      closeModal()
      await uploadImage(blob, fileType, folderPath, handleImageUrls)
    })
  }

  // 画像アップロード直後のバグを修正
  // iOS Safariの場合、Wait処理を入れないとデフォルト画像をうまく切り取れなかった
  useEffect(() => {
    setTimeout(() => {
      if (imageRef && crop && crop.width && crop.height) {
        const scaleX = imageRef.naturalWidth / imageRef.width
        const scaleY = imageRef.naturalHeight / imageRef.height
        canvas.width = crop.width
        canvas.height = crop.height
        const ctx = canvas.getContext('2d')
        if (ctx !== null) {
          ctx.drawImage(
            imageRef,
            (crop.x ?? 0) * scaleX,
            (crop.y ?? 0) * scaleY,
            crop.width * scaleX,
            crop.height * scaleY,
            0,
            0,
            crop.width,
            crop.height
          )
        }
      }
    }, 100)
  }, [imageRef, crop])

  useEffect(() => {
    if (!acceptedFiles || acceptedFiles.length === 0) return
    // アップロード可能枚数よりアップロード中のファイル数が多くなったら状態をリセッt
    if (acceptedFiles.length <= fileIndex) {
      setFileIndex(0)
      setAcceptedFiles([])
      // 次のファイルのImageCropを表示
    } else {
      onSelectFile(acceptedFiles[fileIndex])
    }
  }, [acceptedFiles, fileIndex])

  return (
    <ImageDropzone maxAcceptableFiles={6 - imageUrls.length} handleAcceptedFiles={handleAcceptedFiles}>
      <div className={styles.image__list}>
        {imageUrls.map((imageUrl, index) => (
          <div className={styles.image__list__item} key={imageUrl}>
            <div
              className={classNames({
                [styles.image__list__item__box__store]: imageType === 'store',
                [styles.image__list__item__box__product]: imageType === 'product',
              })}
            >
              <img className={styles.image__list__item__content_uploaded} src={imageUrl} alt={''} />
            </div>
            <Spacer size={8} />
            <FlexRow justifyContent={'center'}>
              <ArrowButton
                disabled={index === 0}
                direction={'prev'}
                onClick={index === 0 ? () => null : () => rearrangeImages(index, 'prev')}
              />
              <Spacer size={20} axis={'horizontal'} />
              <TextButton iconPath={'/icon/blue/delete_15x12.svg'} label={'削除'} onClick={() => deleteImage(index)} />
              <Spacer size={20} axis={'horizontal'} />
              <ArrowButton
                disabled={index === imageUrls.length - 1}
                direction={'next'}
                onClick={index === imageUrls.length - 1 ? () => null : () => rearrangeImages(index, 'next')}
              />
            </FlexRow>
            <Spacer size={8} />
          </div>
        ))}
        {[...Array(6 - imageUrls.length)].map((_, index) => (
          <div className={styles.image__list__item} key={index}>
            <div
              className={classNames({
                [styles.image__list__item__box__store]: imageType === 'store',
                [styles.image__list__item__box__product]: imageType === 'product',
              })}
            >
              <div className={styles.image__list__item__content} />
            </div>
            <Spacer size={8} />
            <FlexRow justifyContent={'center'}>
              <ArrowButton disabled={true} direction={'prev'} onClick={() => null} />
              <Spacer size={20} axis={'horizontal'} />
              <FileUploadButton
                disabled={index !== 0}
                id={`add-image-${imageUrls.length + index}`}
                label={'追加する'}
                onSelectFile={onSelectFile}
                type={'text'}
              />
              <Spacer size={20} axis={'horizontal'} />
              <ArrowButton disabled={true} direction={'next'} onClick={() => null} />
            </FlexRow>
            <Spacer size={8} />
          </div>
        ))}
      </div>
      <Spacer size={16} />
      <FlexColumn alignItems={'center'}>
        <FileUploadButton
          disabled={imageUrls.length === 6}
          id={'button'}
          label={'画像をアップロードする'}
          onSelectFile={onSelectFile}
          type={'button'}
        />
        <Spacer size={12} />
        <p className={`${text.font_small} ${text.color_grey01}`}>※jpeg,jpg,pngの5MB以内の画像をアップロードできます</p>
      </FlexColumn>
      {src && modalType === 'crop' && (
        <ImageModal open={open} closeModal={closeModal}>
          <ReactCrop
            src={src}
            className={image.crop}
            crop={crop}
            ruleOfThirds
            onImageLoaded={onImageLoaded}
            onComplete={onCropComplete}
            onChange={onCropChange}
          />
          <Spacer size={20} />
          <p className={`${text.font_bold} ${text.color_blue_main} ${text.align_center}`}>
            ※白枠の内側のみ保存されます
          </p>
          <Spacer size={20} />
          <FlexRow justifyContent={'center'}>
            <GreyButton label={'キャンセル'} onClick={closeModal} size={'small'} type={'outlined'} />
            <Spacer size={16} />
            <BlueButton label={'保存する'} onClick={saveCroppedImage} size={'small'} type={'main'} />
          </FlexRow>
          {acceptedFiles.length > 1 && (
            <>
              <Spacer size={16} />
              <p className={text.font_bold}>
                {fileIndex + 1}枚目の画像を編集中（{fileIndex + 1}/{acceptedFiles.length}件）
              </p>
            </>
          )}
        </ImageModal>
      )}
      {open && modalType === 'type' && (
        <CautionModal
          cancelLabel={'戻る'}
          caution={'ファイル形式に問題があります'}
          closeModal={closeModal}
          description={`${fileName}はアップロードできません。\njpeg,jpg,pngの5MB以内の画像を選択してください。`}
          open={open}
          onClickCancel={closeModal}
        />
      )}
      {open && modalType === 'size' && (
        <CautionModal
          cancelLabel={'戻る'}
          caution={'画像サイズに問題があります'}
          closeModal={closeModal}
          description={`${fileName}は大き過ぎてアップロードできません。\nファイルのサイズは5.00 MB以下にしてください。`}
          open={open}
          onClickCancel={closeModal}
        />
      )}
    </ImageDropzone>
  )
}

export default ImagesCrop
